import React, { useState, useEffect, useCallback } from 'react';
import usePageTitle from '../hooks/usePageTitle';

import { Link, useNavigate } from 'react-router-dom';
import { getARInvoices, getCustomersList, getCostCenters, exportARInvoices } from '../utils/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const Invoices = () => {
  usePageTitle('AR Invoices');

  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [customers, setCustomers] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    customer_id: '',
    cost_center_id: '',
    approval_status: '',
    accounting_status: '',
    payment_status: '',
    overdue_status: '',
    pnl_date_from: '',
    pnl_date_to: '',
    due_date_from: '',
    due_date_to: '',
  });
  const [exporting, setExporting] = useState(false);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, page: pagination.page, limit: 20, ...filters };
      Object.keys(params).forEach((key) => { if (!params[key]) delete params[key]; });
      const response = await getARInvoices(params);
      setInvoices(response.data.invoices);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Error loading invoices');
    } finally {
      setLoading(false);
    }
  }, [search, pagination.page, filters]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [customersRes, costCentersRes] = await Promise.all([getCustomersList(), getCostCenters()]);
        setCustomers(customersRes.data);
        setCostCenters(costCentersRes.data);
      } catch (error) {
        console.error('Error loading lookups:', error);
      }
    };
    loadLookups();
  }, []);

  const handleSearch = (e) => { setSearch(e.target.value); setPagination((prev) => ({ ...prev, page: 1 })); };
  const handleFilterChange = (key, value) => { setFilters((prev) => ({ ...prev, [key]: value })); setPagination((prev) => ({ ...prev, page: 1 })); };
  const clearFilters = () => { 
    setFilters({ customer_id: '', cost_center_id: '', approval_status: '', accounting_status: '', payment_status: '', overdue_status: '', pnl_date_from: '', pnl_date_to: '', due_date_from: '', due_date_to: '' }); 
    setSearch(''); 
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { ...filters, search };
      Object.keys(params).forEach((key) => { if (!params[key]) delete params[key]; });
      const response = await exportARInvoices(params);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ap_invoices_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed');
    } catch (error) {
      toast.error('Error exporting invoices');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount, currency = 'EUR') => new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount || 0);

  const getApprovalBadge = (status) => {
    const config = {
      draft: { class: 'badge-draft', label: 'Draft' },
      pending_approval: { class: 'badge-pending', label: 'Pending' },
      approved: { class: 'badge-approved', label: 'Approved' },
      rework: { class: 'badge-rework', label: 'Rework' },
      rejected: { class: 'badge-rejected', label: 'Rejected' },
    };
    const c = config[status] || { class: 'badge-draft', label: status };
    return <span className={`badge ${c.class}`}>{c.label}</span>;
  };

  const getPaymentBadge = (status) => {
    return <span className={`badge ${status === 'received' ? 'badge-approved' : 'badge-draft'}`}>{status === 'received' ? 'Received' : 'Not Received'}</span>;
  };

  const getOverdueBadge = (status) => {
    if (status === 'received') return null;
    return <span className={`badge ${status === 'overdue' ? 'badge-rework' : 'badge-draft'}`}>{status === 'overdue' ? 'Overdue' : 'On Time'}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return '-';
    }
  };

  const hasActiveFilters = Object.values(filters).some((v) => v) || search;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">AR Invoices</h1>
          <p className="page-subtitle">{pagination.total} invoices total</p>
        </div>
        <div className="flex gap-md">
          <button className="btn btn-secondary" onClick={handleExport} disabled={exporting}>
            <ArrowDownTrayIcon style={{ width: 18, height: 18 }} />
            {exporting ? 'Exporting...' : 'Export'}
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/ar-invoices/new')}>
            <PlusIcon style={{ width: 18, height: 18 }} />
            New Invoice
          </button>
        </div>
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <MagnifyingGlassIcon className="search-icon" style={{ width: 18, height: 18 }} />
            <input type="text" className="form-input search-input" placeholder="Search by invoice #, reference, description, or vendor..." value={search} onChange={handleSearch} />
          </div>
          <button className={`btn ${showFilters ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setShowFilters(!showFilters)}>
            <FunnelIcon style={{ width: 18, height: 18 }} />
            Filters {hasActiveFilters && <span style={{ marginLeft: 4 }}>•</span>}
          </button>
        </div>

        {showFilters && (
          <div style={{ marginBottom: 'var(--space-lg)', padding: 'var(--space-md)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
            <div className="filters-row" style={{ marginBottom: 'var(--space-md)' }}>
              <div className="filter-group">
                <label>Customer</label>
                <select className="form-select" value={filters.customer_id} onChange={(e) => handleFilterChange('customer_id', e.target.value)}>
                  <option value="">All customers</option>
                  {customers.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
                </select>
              </div>
              <div className="filter-group">
                <label>Cost Center</label>
                <select className="form-select" value={filters.cost_center_id} onChange={(e) => handleFilterChange('cost_center_id', e.target.value)}>
                  <option value="">All cost centers</option>
                  {costCenters.map((cc) => (<option key={cc.id} value={cc.id}>{cc.name}</option>))}
                </select>
              </div>
              <div className="filter-group">
                <label>Approval Status</label>
                <select className="form-select" value={filters.approval_status} onChange={(e) => handleFilterChange('approval_status', e.target.value)}>
                  <option value="">All</option>
                  <option value="draft">Draft</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="approved">Approved</option>
                  <option value="rework">Rework</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Accounting Status</label>
                <select className="form-select" value={filters.accounting_status} onChange={(e) => handleFilterChange('accounting_status', e.target.value)}>
                  <option value="">All</option>
                  <option value="not_sent">Not Sent</option>
                  <option value="sent">Sent</option>
                </select>
              </div>
            </div>
            <div className="filters-row" style={{ marginBottom: 'var(--space-md)' }}>
              <div className="filter-group">
                <label>Payment Status</label>
                <select className="form-select" value={filters.payment_status} onChange={(e) => handleFilterChange('payment_status', e.target.value)}>
                  <option value="">All</option>
                  <option value="not_paid">Not Received</option>
                  <option value="paid">Received</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Overdue Status</label>
                <select className="form-select" value={filters.overdue_status} onChange={(e) => handleFilterChange('overdue_status', e.target.value)}>
                  <option value="">All</option>
                  <option value="overdue">Overdue</option>
                  <option value="before_due">Before Due</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Due Date From</label>
                <input type="date" className="form-input" value={filters.due_date_from} onChange={(e) => handleFilterChange('due_date_from', e.target.value)} />
              </div>
              <div className="filter-group">
                <label>Due Date To</label>
                <input type="date" className="form-input" value={filters.due_date_to} onChange={(e) => handleFilterChange('due_date_to', e.target.value)} />
              </div>
            </div>
            <div className="filters-row" style={{ marginBottom: 'var(--space-md)' }}>
              <div className="filter-group">
                <label>P&L Date From</label>
                <input type="date" className="form-input" value={filters.pnl_date_from} onChange={(e) => handleFilterChange('pnl_date_from', e.target.value)} />
              </div>
              <div className="filter-group">
                <label>P&L Date To</label>
                <input type="date" className="form-input" value={filters.pnl_date_to} onChange={(e) => handleFilterChange('pnl_date_to', e.target.value)} />
              </div>
            </div>
            {hasActiveFilters && (<button className="btn btn-ghost btn-sm" onClick={clearFilters}>Clear all filters</button>)}
          </div>
        )}

        {loading ? (
          <div className="loading-overlay"><div className="spinner" /><span>Loading invoices...</span></div>
        ) : invoices.length > 0 ? (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Due Date</th>
                    <th>Approval</th>
                    <th>Payment</th>
                    <th>Due</th>
                    <th style={{ width: 80 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td><Link to={`/ar-invoices/${invoice.id}`} className="link">{invoice.invoice_number}</Link></td>
                      <td>{invoice.customer_name}</td>
                      <td className="font-mono">{formatCurrency(invoice.amount, invoice.currency)}</td>
                      <td>{format(new Date(invoice.due_date), 'MMM d, yyyy')}</td>
                      <td>{getApprovalBadge(invoice.approval_status)}</td>
                      <td>{getPaymentBadge(invoice.payment_status)}</td>
                      <td>{getOverdueBadge(invoice.overdue_status)}</td>
                      <td>
                        <div className="table-actions">
                          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => navigate(`/ar-invoices/${invoice.id}`)} title="View">
                            <EyeIcon style={{ width: 16, height: 16 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <span className="pagination-info">Page {pagination.page} of {pagination.totalPages} ({pagination.total} items)</span>
                <button className="pagination-btn" disabled={pagination.page === 1} onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}>‹</button>
                {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                  const pageNum = i + 1;
                  return (<button key={pageNum} className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`} onClick={() => setPagination((prev) => ({ ...prev, page: pageNum }))}>{pageNum}</button>);
                })}
                <button className="pagination-btn" disabled={pagination.page === pagination.totalPages} onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}>›</button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <DocumentTextIcon className="empty-state-icon" />
            <p className="empty-state-title">No invoices found</p>
            <p>{hasActiveFilters ? 'Try adjusting your filters' : 'Create your first invoice to get started'}</p>
            {!hasActiveFilters && (<button className="btn btn-primary" onClick={() => navigate('/ar-invoices/new')} style={{ marginTop: '1rem' }}><PlusIcon style={{ width: 18, height: 18 }} />New Invoice</button>)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Invoices;
